import mobileExamples from "libphonenumber-js/examples.mobile.json";
import {
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
} from "libphonenumber-js/max";

export const DEFAULT_PHONE_COUNTRY = "MY";

// Shown at the top of the country list, in this order; the rest follow A-Z.
const PINNED_COUNTRIES = ["MY", "SG", "CN", "TH", "ID", "VN", "BN"];

// Numbers are used for OTP and WhatsApp follow-ups, so a landline is not
// accepted. Some countries (the US, for one) cannot tell the two apart.
const MOBILE_TYPES = ["MOBILE", "FIXED_LINE_OR_MOBILE"];

const parsePhone = (number, country) =>
  parsePhoneNumberFromString(
    String(number ?? ""),
    country || DEFAULT_PHONE_COUNTRY,
  );

export function isValidMobile(number, country) {
  const phone = parsePhone(number, country);

  return Boolean(phone?.isValid() && MOBILE_TYPES.includes(phone.getType()));
}

// +60123456789, whatever spacing or leading zero the number was typed with.
export function toE164(number, country) {
  return parsePhone(number, country)?.number || "";
}

export function formatPhone(number, country) {
  return parsePhone(number, country)?.formatInternational() || "";
}

// Splits a stored number back into the parts the phone field shows.
export function splitPhone(value) {
  const text = String(value || "").trim();
  const phone = parsePhoneNumberFromString(
    text.startsWith("+") ? text : `+${text}`,
  );

  if (!phone) {
    return {
      country: DEFAULT_PHONE_COUNTRY,
      callingCode: `+${getCountryCallingCode(DEFAULT_PHONE_COUNTRY)}`,
      national: text.replace(/\D/g, ""),
    };
  }

  return {
    country: phone.country || DEFAULT_PHONE_COUNTRY,
    callingCode: `+${phone.countryCallingCode}`,
    national: phone.nationalNumber,
  };
}

export function getPhonePlaceholder(country = DEFAULT_PHONE_COUNTRY) {
  return mobileExamples[country] || "";
}

const flagOf = (country) =>
  String.fromCodePoint(
    ...[...country].map((letter) => 0x1f1e6 + letter.charCodeAt(0) - 65),
  );

export function getPhoneCountries(locale = "en") {
  let regionNames;

  try {
    regionNames = new Intl.DisplayNames([locale, "en"], { type: "region" });
  } catch {
    regionNames = null;
  }

  return getCountries()
    .map((country) => ({
      country,
      callingCode: getCountryCallingCode(country),
      flag: flagOf(country),
      name: regionNames?.of(country) || country,
      pinned: PINNED_COUNTRIES.includes(country),
    }))
    .sort((a, b) => {
      if (a.pinned && b.pinned) {
        return (
          PINNED_COUNTRIES.indexOf(a.country) -
          PINNED_COUNTRIES.indexOf(b.country)
        );
      }
      if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;

      return a.name.localeCompare(b.name, locale);
    });
}
